import { z } from 'zod';
import {
  currencySchema,
  deliveryMethodSchema,
  orderStatusSchema,
} from './enums.js';

/* ------------------------------------------------------------------ */
/* Buyer contact                                                       */
/* ------------------------------------------------------------------ */

export const CONTACT_METHODS = ['discord', 'email', 'other'] as const;
export const contactMethodSchema = z.enum(CONTACT_METHODS);
export type ContactMethod = z.infer<typeof contactMethodSchema>;

export const buyerContactSchema = z.object({
  method: contactMethodSchema,
  /** Discord tag / email / other handle we reach the buyer on. */
  handle: z.string().trim().min(2).max(160),
  /** Optional in-game username used for delivery. */
  robloxUsername: z.string().trim().max(60).optional(),
});

export type BuyerContact = z.infer<typeof buyerContactSchema>;

/* ------------------------------------------------------------------ */
/* Order line items                                                    */
/* ------------------------------------------------------------------ */

export const orderItemSchema = z.object({
  id: z.string().uuid(),
  orderId: z.string().uuid(),
  /** null once the product has been deleted from the catalog. */
  productId: z.string().uuid().nullable(),
  /** Snapshot of product identity at purchase time (survives edits/deletes). */
  productName: z.string(),
  productSlug: z.string(),
  qty: z.number().int().min(1),
  /** Price captured at purchase — never re-derived from the product. */
  unitPriceCents: z.number().int().min(0),
  lineTotalCents: z.number().int().min(0),
});

export type OrderItem = z.infer<typeof orderItemSchema>;

/* ------------------------------------------------------------------ */
/* Order domain object (API response)                                  */
/* ------------------------------------------------------------------ */

export const orderSchema = z.object({
  id: z.string().uuid(),
  status: orderStatusSchema,
  subtotalCents: z.number().int().min(0),
  currency: currencySchema,
  buyerContact: buyerContactSchema,
  deliveryMethod: deliveryMethodSchema,
  internalNotes: z.string().nullable(),
  items: z.array(orderItemSchema),
  createdAt: z.string().datetime(),
  updatedAt: z.string().datetime(),
});

export type Order = z.infer<typeof orderSchema>;

/**
 * Public confirmation view — the order plus payment instructions returned by
 * the active PaymentProvider. Deliberately omits internal notes.
 */
export const orderConfirmationSchema = z.object({
  order: orderSchema.omit({ internalNotes: true }),
  payment: z.object({
    provider: z.string(),
    status: z.enum(['pending', 'confirmed']),
    /** Human-facing instructions (e.g. "message us on Discord to complete"). */
    instructions: z.string(),
    reference: z.string(),
    /** Hosted checkout page to send the buyer to (crypto gateways). */
    checkoutUrl: z.string().url().optional(),
    /** Manual-completion CTA (e.g. Discord) when there is no gateway. */
    contactUrl: z.string().url().optional(),
  }),
});

export type OrderConfirmation = z.infer<typeof orderConfirmationSchema>;

/* ------------------------------------------------------------------ */
/* Checkout input (public)                                             */
/* ------------------------------------------------------------------ */

export const checkoutItemInputSchema = z.object({
  productId: z.string().uuid(),
  qty: z.number().int().min(1).max(999),
});

export type CheckoutItemInput = z.infer<typeof checkoutItemInputSchema>;

export const checkoutInputSchema = z.object({
  items: z.array(checkoutItemInputSchema).min(1).max(50),
  buyerContact: buyerContactSchema,
  deliveryMethod: deliveryMethodSchema,
});

export type CheckoutInput = z.infer<typeof checkoutInputSchema>;

/* ------------------------------------------------------------------ */
/* Admin order mutations                                               */
/* ------------------------------------------------------------------ */

export const updateOrderInputSchema = z
  .object({
    /** Requested state-machine transition target. */
    status: orderStatusSchema.optional(),
    internalNotes: z.string().max(4000).nullable().optional(),
  })
  .refine((v) => v.status !== undefined || v.internalNotes !== undefined, {
    message: 'Provide at least one of status or internalNotes',
  });

export type UpdateOrderInput = z.infer<typeof updateOrderInputSchema>;

export const orderListQuerySchema = z.object({
  status: orderStatusSchema.optional(),
  page: z.coerce.number().int().min(1).default(1),
  pageSize: z.coerce.number().int().min(1).max(100).default(25),
});

export type OrderListQuery = z.infer<typeof orderListQuerySchema>;
